import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import './Tooltip.scss';

const Tooltip = ({
  children,
  content,
  direction = 'top',
  className = '',
  delay = 200,
  disabled = false,
  autoHideDelay = 2500
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const timeoutRef = useRef(null);
  const autoHideTimeoutRef = useRef(null);
  const tooltipRef = useRef(null);
  const triggerRef = useRef(null);

  const showTooltip = () => {
    if (disabled || !content) return;

    timeoutRef.current = setTimeout(() => {
      setIsVisible(true);
    }, delay);
  };

  const hideTooltip = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (autoHideTimeoutRef.current) {
      clearTimeout(autoHideTimeoutRef.current);
      autoHideTimeoutRef.current = null;
    }
    setIsVisible(false);
  };

  // Touch has no real "hover" — tapping the trigger shows the tooltip the same way desktop hover
  // does, but there's no equivalent of moving the mouse away to close it again, so it would
  // otherwise stay open until the user happens to tap something else entirely. Auto-close it after
  // a few seconds instead — but only for a touch-originated show; desktop hover (mouseenter/focus
  // below) is untouched and keeps the tooltip open for as long as the pointer actually stays over it.
  const handleTouchStart = () => {
    showTooltip();
    if (autoHideTimeoutRef.current) clearTimeout(autoHideTimeoutRef.current);
    autoHideTimeoutRef.current = setTimeout(hideTooltip, delay + autoHideDelay);
  };

  useEffect(() => {
    if (isVisible && tooltipRef.current && triggerRef.current) {
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      const spacing = 8;

      let newPosition = { top: 0, left: 0 };

      switch (direction) {
        case 'top':
          newPosition = {
            top: triggerRect.top - tooltipRect.height - spacing,
            left: triggerRect.left + (triggerRect.width - tooltipRect.width) / 2
          };
          break;
        case 'bottom':
          newPosition = {
            top: triggerRect.bottom + spacing,
            left: triggerRect.left + (triggerRect.width - tooltipRect.width) / 2
          };
          break;
        case 'left':
          newPosition = {
            top: triggerRect.top + (triggerRect.height - tooltipRect.height) / 2,
            left: triggerRect.left - tooltipRect.width - spacing
          };
          break;
        case 'right':
          newPosition = {
            top: triggerRect.top + (triggerRect.height - tooltipRect.height) / 2,
            left: triggerRect.right + spacing
          };
          break;
        default:
          break;
      }

      // Adjust position to prevent tooltip from going off screen
      const padding = 10;
      newPosition.left = Math.max(padding, Math.min(newPosition.left, window.innerWidth - tooltipRect.width - padding));
      newPosition.top = Math.max(padding, Math.min(newPosition.top, window.innerHeight - tooltipRect.height - padding));

      setPosition(newPosition);
    }
  }, [isVisible, direction]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (autoHideTimeoutRef.current) {
        clearTimeout(autoHideTimeoutRef.current);
      }
    };
  }, []);

  // The tooltip is `position: fixed` at a coordinate computed once when it opens (see above) — it
  // never re-tracks the trigger, so scrolling (any ancestor, not just window, hence `capture` +
  // `true` for the third arg) leaves it floating over unrelated content instead of following the
  // element it's anchored to. Simplest fix: just close it, same as mouse-leave/blur already do.
  useEffect(() => {
    if (!isVisible) return;

    window.addEventListener('scroll', hideTooltip, true);
    return () => window.removeEventListener('scroll', hideTooltip, true);
  }, [isVisible]);

  return (
    <div className={`tooltip-wrapper ${className}`}>
      <div
        ref={triggerRef}
        className='tooltip-trigger'
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
        onFocus={showTooltip}
        onBlur={hideTooltip}
        onTouchStart={handleTouchStart}
      >
        {children}
      </div>
      {isVisible && content && (
        <div
          ref={tooltipRef}
          className={`tooltip-content tooltip-${direction}`}
          style={{
            position: 'fixed',
            top: `${position.top}px`,
            left: `${position.left}px`,
            zIndex: 9999
          }}
          role='tooltip'
        >
          <div className='tooltip-inner'>{content}</div>
          <div className={`tooltip-arrow tooltip-arrow-${direction}`} />
        </div>
      )}
    </div>
  );
};

Tooltip.propTypes = {
  children: PropTypes.node.isRequired,
  content: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  direction: PropTypes.oneOf(['top', 'bottom', 'left', 'right']),
  className: PropTypes.string,
  delay: PropTypes.number,
  disabled: PropTypes.bool,
  autoHideDelay: PropTypes.number
};

export default Tooltip;
